import React, { useState } from 'react';
import { toast } from "sonner";
import { scrollToTop } from '../lib/utils';
import { emailService } from '../Api/EmailService';
import { Button } from '../components/ui/button';
import SecurityQuestion, { useSecurityQuestion } from '../components/SecurityQuestion';
import { hasText, stripLeadingSpaces, trimFormData, emailError, errorClass, fieldA11y, requiredName, requiredText, requiredSubject, optionalCompany, optionalPhoneError, escapeFormData } from '../lib/formValidation';
import BotTrap, { useBotTrap } from '../components/BotTrap';
import FieldError from '../components/FieldError';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Mail, MapPin, Clock, CheckCircle } from 'lucide-react';
import { contactInfo } from '../data/mockData';

// Shared field styling: gray field background, same blue as the card border on focus
const FIELD_CLASS =
  "bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 " +
  "data-[placeholder]:text-slate-400 " +
  "focus:border-blue-500 focus:ring-1 focus:ring-blue-500 " +
  "focus-visible:border-blue-500 focus-visible:ring-1 focus-visible:ring-blue-500 " +
  // stop Chrome autofill from painting the field light blue
  "[&:-webkit-autofill]:[-webkit-text-fill-color:#fff] " +
  "[&:-webkit-autofill]:shadow-[inset_0_0_0_1000px_#1e293b] " +
  "[&:-webkit-autofill:focus]:shadow-[inset_0_0_0_1000px_#1e293b]";

// Dropdown list: same gray as the fields
const DROPDOWN_CLASS =
  "bg-slate-800 border-slate-700 text-white " +
  "[&_[role=option]]:text-white " +
  "[&_[role=option][data-highlighted]]:bg-slate-700 [&_[role=option][data-highlighted]]:text-white " +
  "[&_[role=option]:focus]:bg-slate-700 [&_[role=option]:focus]:text-white";

// Message for each required field (null = valid)
const validators = {
  firstName: (v) => requiredName(v, 'First name', 25),
  lastName: (v) => requiredName(v, 'Last name', 25),
  email: (v) => emailError(v),
  company: (v) => optionalCompany(v, 'Company', 50),
  phone: (v) => optionalPhoneError(v),
  subject: (v) => requiredSubject(v, 'Subject', 100),
  message: (v) => requiredText(v, 'Message', 2500),
};

const ContactPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: '',
    inquiryType: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Show a field's message only after the user has visited it
  const [fieldTouched, setFieldTouched] = useState({});
  // A field shows its message once the user has visited it or typed something,
  // so they always know why the button is disabled
  const getError = (field) => {
    const show = fieldTouched[field] || hasText(formData[field]);
    return show ? validators[field](formData[field]) : null;
  };
  const captcha = useSecurityQuestion();
  const trap = useBotTrap();

  // Button stays disabled until every required field has real text
  // (spaces only do not count) and the security question is answered
  const canSubmit =
    Object.keys(validators).every((field) => !validators[field](formData[field])) &&
    captcha.isCorrect;

  const createEmailPayload = (rawData) => {
    // everything typed by the visitor is escaped before it goes into the HTML email
    const data = escapeFormData(rawData);
    const subject = `New Contact Form Submission from ${rawData.firstName} ${rawData.lastName}`;

    const template = `<!DOCTYPE html PUBLIC "-//W3C//DTD HTML 4.01 Transitional//EN">
<html>
  <head>
    <meta content="text/html;charset=UTF-8" http-equiv="Content-Type">
    <link href="https://fonts.googleapis.com/css2?family=Noto+Sans:ital,wght@0,100..900;1,100..900&display=swap" rel="stylesheet">
    <style>
      body {
        font-family: 'Noto Sans', sans-serif;
        color: #000000;
      }
      h2 {
        font-family: 'Noto Sans', sans-serif;
      }
      p {
        font-family: 'Noto Sans', sans-serif;
        margin: 4px 0;
      }
    </style>
  </head>
  <body>
    <div>
      <h2>New Contact</h2>
     <p><strong>Name:</strong> ${data.firstName} ${data.lastName}</p>
          <p><strong>Company:</strong> ${data.company}</p>
          <p><strong>Email:</strong> ${data.email}</p>
          <p><strong>Phone:</strong> ${data.phone}</p>
          <p><strong>Inquiry Type:</strong> ${data.inquiryType}</p>
          <p><strong>Subject:</strong> ${data.subject}</p>
          <p><strong>Message:</strong> ${data.message}</p>
    </div>
  </body>
</html>`;
    return { subject, template };
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || isSubmitting) return;
    if (trap.looksLikeBot()) {
      // pretend it worked, send nothing
      setIsSubmitted(true);
      return;
    }
    setIsSubmitted(false)
    setIsSubmitting(true)
    const payload = createEmailPayload(trimFormData(formData));
    const promise = emailService.sendEmail(payload); // this returns a promise

    // Show toast for this promise
    toast.promise(promise, {
      loading: "Sending message...",
      success: "Message sent successfully!",
      error: "Failed to send message. Try again later.",
    });

    // Handle the result of the promise
    try {
      await promise;
      captcha.reset();
      setIsSubmitted(true);
      scrollToTop()
    }
    catch (error) {
      captcha.reset();
      setIsSubmitted(false)
      setIsSubmitting(false)
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      // no leading spaces, so a field can never be "only spaces"
      [field]: stripLeadingSpaces(value)
    }));
  };

  // Trim trailing spaces when the user leaves any text field of the form
  const handleFormBlur = (e) => {
    const { id, value, tagName } = e.target;
    if (id in validators) setFieldTouched(prev => ({ ...prev, [id]: true }));
    if ((tagName === 'INPUT' || tagName === 'TEXTAREA') && id in formData && typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed !== value) handleChange(id, trimmed);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen section-padding">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 rounded-full bg-green-400/10 flex items-center justify-center mx-auto mb-8">
              <CheckCircle className="h-10 w-10 text-green-400" />
            </div>
            <h1 className="display-md text-white mb-6">Message Sent Successfully!</h1>
            <p className="body-lg mb-8">
              Thank you for contacting us. We've received your message and will get back to you within 24 hours.
            </p>
            <Button className="btn-secondary" onClick={() => {
              setIsSubmitted(false)
              setFieldTouched({});
              setFormData({
                firstName: '',
                lastName: '',
                email: '',
                company: '',
                phone: '',
                subject: '',
                message: '',
                inquiryType: ''
              });
            }}>
              Send Another Message
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen section-padding">
      <div className="container">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="display-md text-white mb-6">Get In Touch</h1>
            <p className="body-lg max-w-2xl mx-auto">
              Have questions about GrowSquare? Need help getting started? Our team is here to help.
              Reach out and we'll get back to you within 24 hours.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="feature-card">
                <CardHeader>
                  <CardTitle className="h2 text-white">Send us a Message</CardTitle>
                  <CardDescription className="body-md">
                    Fill out the form below and we'll get back to you as soon as possible.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} onBlur={handleFormBlur} className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="firstName" className="text-white">First Name *</Label>
                        <Input
                          id="firstName"
                          autoComplete="given-name"
                          value={formData.firstName}
                          onChange={(e) => handleChange('firstName', e.target.value)}
                          required
                          {...fieldA11y('firstName', getError('firstName'))}
                          className={`${FIELD_CLASS} ${errorClass(getError('firstName'))}`}
                        />
                        <FieldError id="firstName-error" message={getError('firstName')} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lastName" className="text-white">Last Name *</Label>
                        <Input
                          id="lastName"
                          autoComplete="family-name"
                          value={formData.lastName}
                          onChange={(e) => handleChange('lastName', e.target.value)}
                          required
                          {...fieldA11y('lastName', getError('lastName'))}
                          className={`${FIELD_CLASS} ${errorClass(getError('lastName'))}`}
                        />
                        <FieldError id="lastName-error" message={getError('lastName')} />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-white">Email Address *</Label>
                      <Input
                        id="email"
                        autoComplete="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        required
                        {...fieldA11y('email', getError('email'))}
                        className={`${FIELD_CLASS} ${errorClass(getError('email'))}`}
                      />
                        <FieldError id="email-error" message={getError('email')} />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="company" className="text-white">Company</Label>
                        <Input
                          id="company"
                          autoComplete="organization"
                          value={formData.company}
                          onChange={(e) => handleChange('company', e.target.value)}
                          {...fieldA11y('company', getError('company'))}
                          className={`${FIELD_CLASS} ${errorClass(getError('company'))}`}
                        />
                        <FieldError id="company-error" message={getError('company')} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="text-white">Phone Number</Label>
                        <Input
                          id="phone"
                          autoComplete="tel"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => handleChange('phone', e.target.value)}
                          {...fieldA11y('phone', getError('phone'))}
                          className={`${FIELD_CLASS} ${errorClass(getError('phone'))}`}
                        />
                        <FieldError id="phone-error" message={getError('phone')} />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="inquiryType" className="text-white">Inquiry Type</Label>
                      <Select value={formData.inquiryType} onValueChange={(value) => handleChange('inquiryType', value)}>
                        <SelectTrigger id="inquiryType" className={FIELD_CLASS}>
                          <SelectValue placeholder="Select inquiry type" />
                        </SelectTrigger>
                        <SelectContent className={DROPDOWN_CLASS}>
                          <SelectItem value="sales">Sales Inquiry</SelectItem>
                          <SelectItem value="support">Technical Support</SelectItem>
                          <SelectItem value="partnership">Partnership</SelectItem>
                          <SelectItem value="general">General Question</SelectItem>
                          <SelectItem value="demo">Product Demo</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="subject" className="text-white">Subject *</Label>
                      <Input
                        id="subject"
                        value={formData.subject}
                        onChange={(e) => handleChange('subject', e.target.value)}
                        required
                        {...fieldA11y('subject', getError('subject'))}
                        className={`${FIELD_CLASS} ${errorClass(getError('subject'))}`}
                        placeholder="Brief description of your inquiry"
                      />
                        <FieldError id="subject-error" message={getError('subject')} />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message" className="text-white">Message *</Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => handleChange('message', e.target.value)}
                        required
                        {...fieldA11y('message', getError('message'))}
                        className={`${FIELD_CLASS} ${errorClass(getError('message'))} min-h-[120px]`}
                        placeholder="Please provide details about your inquiry..."
                      />
                        <FieldError id="message-error" message={getError('message')} />
                    </div>
                    <BotTrap trap={trap} />
                    <SecurityQuestion captcha={captcha} fieldClass={FIELD_CLASS} />
                    <Button type="submit" className="btn-primary w-full glow-effect" disabled={isSubmitting || !canSubmit}>
                      {isSubmitting ? "Sending..." : "Send Message"}
                    </Button>
                    {/* <Button type="submit" className="btn-primary w-full glow-effect">
                      Send Message
                    </Button> */}
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Information */}
            <div className="space-y-6">
              {/* Contact Methods */}
              <Card className="feature-card">
                <CardHeader>
                  <CardTitle className="h3 text-white">Contact Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <Mail className="h-5 w-5 text-green-400 mt-1" />
                    <div>
                      <p className="body-md font-medium text-white">Email</p>
                      <p className="body-sm text-slate-400">{contactInfo.email.general}</p>
                      {/* <p className="body-sm text-slate-400">{contactInfo.email.sales}</p> */}
                    </div>
                  </div>
                  {/* <div className="flex items-start space-x-3">
                    <Phone className="h-5 w-5 text-green-400 mt-1" />
                    <div>
                      <p className="body-md font-medium text-white">Phone</p>
                      <p className="body-sm text-slate-400">{contactInfo.addresses[0].phone}</p>
                 <p className="body-sm text-slate-400">{contactInfo.addresses[1].phone}</p>
                    </div>
                  </div> */}
                  <div className="flex items-start space-x-3">
                    <MapPin className="h-5 w-5 text-green-400 mt-1" />
                    <div>
                      <p className="body-md font-medium text-white">Headquarters</p>
                      <p className="body-sm text-slate-400">{contactInfo.addresses[0].address}</p>
                      <p className="body-sm text-slate-400">{contactInfo.addresses[0].city}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Clock className="h-5 w-5 text-green-400 mt-1" />
                    <div>
                      <p className="body-md font-medium text-white">Business Hours</p>
                      <p className="body-sm text-slate-400">Mon-Fri: 9AM - 6PM PST</p>
                      <p className="body-sm text-slate-400">24/7 Support Available</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Support Options */}
              {/* <Card className="feature-card">
                <CardHeader>
                  <CardTitle className="h3 text-white">Other Ways to Reach Us</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <MessageSquare className="h-5 w-5 text-green-400" />
                    <div>
                      <p className="body-md font-medium text-white">Live Chat</p>
                      <p className="body-sm text-slate-400">Available on our website</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Users className="h-5 w-5 text-green-400" />
                    <div>
                      <p className="body-md font-medium text-white">Community Forum</p>
                      <p className="body-sm text-slate-400">Connect with other users</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Headphones className="h-5 w-5 text-green-400" />
                    <div>
                      <p className="body-md font-medium text-white">Priority Support</p>
                      <p className="body-sm text-slate-400">For enterprise customers</p>
                    </div>
                  </div>
                </CardContent>
              </Card> */}

              {/* Office Locations */}
              <Card className="feature-card">
                <CardHeader>
                  <CardTitle className="h3 text-white">Office Locations</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {contactInfo.addresses.map((address, index) => (
                    <div key={index} className="border-b border-slate-700 pb-4 last:border-b-0 last:pb-0">
                      <p className="body-md font-medium text-white mb-1">{address.office}</p>
                      <p className="body-sm text-slate-400">{address.address}</p>
                      <p className="body-sm text-slate-400">{address.city}</p>
                      <p className="body-sm text-green-400">{address.phone}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default ContactPage;